import { useFeatureFlag } from "contexts/FeatureFlagContext";
import _ from "lodash";
import { useMemo } from "react";
import { getEmptyCancellableRequest } from "restAPI";
import FindingAPI from "restAPI/FindingAPI";
import { AQLMetadataDTO } from "types/api/aqlMetadataDto";
import { AqlSupportedFieldsDto } from "types/api/aqlSupportedFieldsDto";
import { BaseResponseDto } from "types/api/baseResponseDto";

import useAPIResponse from "./useAPIResponse";

export default function useAQLFields({
  bypassFeatureFlag = false,
  includeRegexOperators = false,
}: { bypassFeatureFlag?: boolean; includeRegexOperators?: boolean } = {}) {
  const featureFlags = useFeatureFlag();

  const [aqlSupportedFieldsRes, aqlSupportedFieldsResLoading] = useAPIResponse<
    BaseResponseDto<AqlSupportedFieldsDto>
  >(FindingAPI.getAQLSupportedFields, []);

  const getAQLMetadata = useMemo(
    () =>
      bypassFeatureFlag || featureFlags.FINDING_AQL_V2
        ? () => FindingAPI.getAQLMetadataV2({ includeRegexOperators })
        : getEmptyCancellableRequest,

    [bypassFeatureFlag, featureFlags.FINDING_AQL_V2, includeRegexOperators],
  );

  const [aqlMetadata, aqlMetadataLoading] = useAPIResponse<
    BaseResponseDto<AQLMetadataDTO>
  >(getAQLMetadata, [getAQLMetadata]);

  const aqlSupportedFields = useMemo(
    () => Object.keys(aqlSupportedFieldsRes?.data.basicToAQLMap ?? {}),
    [aqlSupportedFieldsRes?.data.basicToAQLMap],
  );

  const aqlConversionSupportedBasicFields = useMemo(
    () => Object.values(aqlSupportedFieldsRes?.data.basicToAQLMap ?? {}),
    [aqlSupportedFieldsRes?.data.basicToAQLMap],
  );

  const basicToAQLMap = useMemo(
    () => aqlSupportedFieldsRes?.data.basicToAQLMap ?? {},
    [aqlSupportedFieldsRes?.data.basicToAQLMap],
  );

  const aqlOperatorSupport = useMemo(() => {
    return {
      comparisonOperatorSupportedFields: _.compact(
        _.map(
          aqlSupportedFieldsRes?.data.comparisonOperatorSupportedFields,
          (field) =>
            _.findKey(
              aqlSupportedFieldsRes?.data.basicToAQLMap,
              (value) => value === field,
            ) as string,
        ),
      ),
      inOperatorSupportedFields: _.compact(
        _.map(
          aqlSupportedFieldsRes?.data.inOperatorSupportedFields,
          (field) =>
            _.findKey(
              aqlSupportedFieldsRes?.data.basicToAQLMap,
              (value) => value === field,
            ) as string,
        ),
      ),
      likeOperatorSupportedFields: _.compact(
        _.map(
          aqlSupportedFieldsRes?.data.likeOperatorSupportedFields,
          (field) =>
            _.findKey(
              aqlSupportedFieldsRes?.data.basicToAQLMap,
              (value) => value === field,
            ) as string,
        ),
      ),
    };
  }, [aqlSupportedFieldsRes?.data]);

  return {
    aqlSupportedFieldsResLoading:
      aqlMetadataLoading || aqlSupportedFieldsResLoading,
    aqlSupportedFields,
    aqlOperatorSupport,
    aqlConversionSupportedBasicFields,
    basicToAQLMap,
    aqlMetadata: aqlMetadata?.data,
  };
}
